// services/project.service.js
import httpClient from './httpClient';
import TokenService from './token.service';
import { extractDetailMessage, extractDetailError } from '../utils/errorDetail';

/**
 * Project Service
 * Maneja todas las operaciones relacionadas con Projects
 * Base URL configured via REACT_APP_AUTH_API_URL or REACT_APP_API_BASE_URL
 */

// Usar cliente HTTP centralizado
const authClient = httpClient;

/**
 * Normalizes error payloads from modern HTTP-status-based responses.
 * The new backend returns `error` as a string; legacy endpoints may return an array.
 * @param {Error} error - Axios error object
 * @param {string} fallback - Fallback message
 * @returns {string[]}
 */
const normalizeProjectError = (error, fallback = 'Project operation failed') => {
  const errorData = error.response?.data;
  const raw = errorData?.error;
  if (Array.isArray(raw) && raw.length > 0) return raw;
  if (raw && typeof raw === 'string') return [raw];
  if (errorData?.message) return [errorData.message];
  if (errorData?.detail) return [extractDetailMessage(errorData.detail)];
  return [fallback];
};

/**
 * Sobre de fallo común a todos los métodos. Sobre el mensaje aplanado agrega
 * `code` y `context`, que es lo que `resolveAccountDomainError` necesita para
 * elegir el copy sin leer el texto del mensaje.
 * @param {Error} error - Axios error object
 * @param {string} fallback - Mensaje cuando la respuesta no trae ninguno
 * @returns {{success: false, status: number|undefined, code: string|null, context: object|null, error: string[]}}
 */
const projectErrorResult = (error, fallback) => {
  const { code, context } = extractDetailError(error.response?.data?.detail);
  return {
    success: false,
    status: error.response?.status,
    code,
    context,
    error: normalizeProjectError(error, fallback),
  };
};

const ProjectService = {
  /**
   * Crear nuevo proyecto
   * POST /projects
   * Body: { name, description, account_id?, organization_id? }
   */
  async createProject(projectData) {
    try {
      // Preparar el payload completo
      const payload = {
        name: projectData.name,
        description: projectData.description || ''
      };
      
      // Incluir account_id solo si existe y no es undefined
      if (projectData.account_id && projectData.account_id !== undefined) {
        payload.account_id = projectData.account_id;
      }
      
      // Incluir organization_id solo si existe y no es undefined
      if (projectData.organization_id && projectData.organization_id !== undefined) {
        payload.organization_id = projectData.organization_id;
      }
      
      // Verificar que tenemos exactamente uno de account_id u organization_id
      if (!payload.account_id && !payload.organization_id) {
        return {
          success: false,
          error: ['account_id or organization_id is required for project creation']
        };
      }
      
      const response = await authClient.post('/projects', payload);
      
      const body = response.data?.body ?? response.data;
      return {
        success: true,
        data: {
          id: body.project_id,
          project_id: body.project_id,
          name: body.name,
          description: body.description,
          owner_id: body.owner_id,
          account_id: body.account_id,
          organization_id: body.organization_id,
          status: body.status,
          created_at: body.created_at
        }
      };
    } catch (error) {
      return projectErrorResult(error, 'Failed to create project');
    }
  },

  /**
   * Obtener detalles de un proyecto
   * GET /projects/{project_id}
   */
  async getProject(projectId) {
    try {
      const response = await authClient.get(`/projects/${projectId}`);
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error fetching project:', error);
      return projectErrorResult(error, 'Failed to fetch project');
    }
  },

  /**
   * Actualizar proyecto
   * PUT /projects/{project_id}
   */
  async updateProject(projectId, updates) {
    try {
      const response = await authClient.put(`/projects/${projectId}`, updates);
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error updating project:', error);
      return projectErrorResult(error, 'Failed to update project');
    }
  },

  /**
   * Eliminar proyecto (soft delete)
   * DELETE /projects/{project_id}
   */
  async deleteProject(projectId) {
    try {
      await authClient.delete(`/projects/${projectId}`);
      return {
        success: true
      };
    } catch (error) {
      console.error('Error deleting project:', error);
      return projectErrorResult(error, 'Failed to delete project');
    }
  },

  /**
   * Listar proyectos personales
   * GET /projects?account_id={account_id}&page=1&page_size=20
   */
  async listPersonalProjects({ accountId, page = 1, pageSize = 10 } = {}) {
    try {
      // Si no se proporciona accountId, intentar obtenerlo del usuario actual
      if (!accountId) {
        const userData = TokenService.getUserData();
        accountId = userData?.account_id || userData?.default_account_id;
      }
      
      if (!accountId) {
        console.error('No account_id available for listing projects');
        return {
          success: false,
          error: ['No account_id provided'],
          data: [],
          totalCount: 0
        };
      }
      
      const response = await authClient.get('/projects', {
        params: {
          account_id: accountId,
          page,
          page_size: pageSize
        }
      });
      
      const raw = response.data?.body ?? response.data;
      // Supports array-at-root and { projects: [...] } shapes
      const projects = Array.isArray(raw) ? raw : raw?.projects;
      if (!Array.isArray(projects)) {
        return {
          success: false,
          error: ['Invalid projects response format'],
          data: [],
          totalCount: 0
        };
      }
      
      return {
        success: true,
        data: projects.map(p => ({
          id: p.project_id,
          project_id: p.project_id,
          name: p.name,
          description: p.description,
          status: p.status,
          owner_id: p.owner_id,
          account_id: p.account_id,
          organization_id: p.organization_id,
          created_at: p.created_at,
          updated_at: p.updated_at
        })),
        totalCount: raw?.total_count ?? projects.length,
        page: raw?.page ?? page,
        pageSize: raw?.page_size ?? pageSize
      };
    } catch (error) {
      console.error('Error listing personal projects:', error);
      return { ...projectErrorResult(error, 'Failed to list personal projects'), data: [], totalCount: 0 };
    }
  },

  /**
   * Listar proyectos de organización
   * GET /projects?account_id={account_id}&page=1&page_size=20
   * Para organizaciones, usar el account_id de la organización
   */
  async listOrganizationProjects(organizationId, { page = 1, pageSize = 10 } = {}) {
    try {
      const response = await authClient.get('/projects', {
        params: {
          organization_id: organizationId,
          page,
          page_size: pageSize
        }
      });
      
      const raw = response.data?.body ?? response.data;
      const projects = Array.isArray(raw) ? raw : raw?.projects;
      if (!Array.isArray(projects)) {
        return {
          success: false,
          error: ['Invalid projects response format'],
          data: [],
          totalCount: 0
        };
      }
      
      return {
        success: true,
        data: projects.map(p => ({
          id: p.project_id,
          project_id: p.project_id,
          name: p.name,
          description: p.description,
          status: p.status,
          owner_id: p.owner_id,
          account_id: p.account_id,
          organization_id: p.organization_id,
          created_at: p.created_at,
          updated_at: p.updated_at
        })),
        totalCount: raw?.total_count ?? projects.length
      };
    } catch (error) {
      console.error('Error listing organization projects:', error);
      return { ...projectErrorResult(error, 'Failed to list organization projects'), data: [], totalCount: 0 };
    }
  },

  /**
   * Listar proyectos de una cuenta específica
   * GET /projects?account_id={account_id}&page=1&page_size=20
   */
  async listAccountProjects(accountId, { page = 1, pageSize = 10 } = {}) {
    try {
      const response = await authClient.get('/projects', {
        params: {
          account_id: accountId,
          page,
          page_size: pageSize
        }
      });
      
      const raw = response.data?.body ?? response.data;
      const projects = Array.isArray(raw) ? raw : raw?.projects;
      if (!Array.isArray(projects)) {
        return {
          success: false,
          error: ['Invalid projects response format'],
          data: [],
          totalCount: 0
        };
      }
      
      return {
        success: true,
        data: projects.map(p => ({
          id: p.project_id,
          project_id: p.project_id,
          name: p.name,
          description: p.description,
          status: p.status,
          owner_id: p.owner_id,
          account_id: p.account_id,
          organization_id: p.organization_id,
          created_at: p.created_at,
          updated_at: p.updated_at
        })),
        totalCount: raw?.total_count ?? projects.length
      };
    } catch (error) {
      console.error('Error listing account projects:', error);
      return { ...projectErrorResult(error, 'Failed to list account projects'), data: [], totalCount: 0 };
    }
  },

  /**
   * Listar todos los proyectos del usuario
   * GET /projects?page=1&page_size=20
   */
  async listAllProjects({ page = 1, pageSize = 20 } = {}) {
    try {
      const response = await authClient.get('/projects', {
        params: {
          page,
          page_size: pageSize
        }
      });
      const raw = response.data?.body ?? response.data;
      return {
        success: true,
        data: Array.isArray(raw) ? raw : (raw?.projects ?? []),
        totalCount: raw?.total_count ?? 0
      };
    } catch (error) {
      console.error('Error listing all projects:', error);
      return { ...projectErrorResult(error, 'Failed to list all projects'), data: [], totalCount: 0 };
    }
  }
};

export default ProjectService;
